/**
 * MarkdownRenderer — adapted from mind_chat_plugin/frontend-v2 for the
 * vibe-designer ChatPanel. Adds rehype-highlight for syntax highlighting and
 * preserves the language-tag + copy-button UX the chat panel had pre-fix.
 *
 * Pre-fix the chat panel used a homemade renderer that only matched fenced
 * code blocks via `/```(\w+)?\n([\s\S]*?)```/g`. Inline code, lists, headings,
 * and paragraph breaks all collapsed into a single wall of text.
 */
import { memo, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import type { Components } from 'react-markdown';
import { Copy } from 'lucide-react';
import 'highlight.js/styles/github-dark.css';
import { FileWriteCard } from './FileWriteCard';

/** Detect the LLM file-write protocol: ```file:<path> ... ```
 * react-markdown forwards the fence language as className `language-<lang>`
 * on the inner <code>. For paths the language ends up like
 * `language-file:src/Hello.tsx` (slashes/dots are valid in CSS class names).
 */
function extractFileWritePath(child: React.ReactNode): string | null {
  if (!child || typeof child !== 'object' || !('props' in child)) return null;
  const cls = (child as { props?: { className?: unknown } }).props?.className;
  if (typeof cls !== 'string') return null;
  const match = cls.match(/language-file:(.+?)(?:\s|$)/);
  return match ? match[1] : null;
}

export interface MarkdownRendererProps {
  content: string;
  className?: string;
  // 2026-05-06 truncation-bundle Fix #3 (R1 reviewer C5):
  // closure-captured by the components map so the `pre:` override can
  // pass them down to FileWriteCard. The chat-specific concept does
  // couple MarkdownRenderer to chat semantics, but this is more
  // traceable than React context (Provider overhead for one scalar)
  // or a global zustand store (hidden coupling).
  messageFinishReason?: string;
  messageIsStreaming?: boolean;
}

/**
 * Custom <pre> wrapper: language tag header + copy button. The inner
 * <code> retains rehype-highlight's `hljs language-<lang>` className so
 * the github-dark theme styles apply.
 */
function CodeBlock({ children }: { children: React.ReactNode }) {
  const preRef = useRef<HTMLPreElement>(null);

  // Walk the React tree to find the language class on the <code> child.
  // rehype-highlight emits <code class="hljs language-typescript">…</code>.
  let language = 'text';
  const child = Array.isArray(children) ? children[0] : children;
  if (
    child &&
    typeof child === 'object' &&
    'props' in child &&
    typeof (child as { props?: { className?: unknown } }).props?.className === 'string'
  ) {
    const cls = (child as { props: { className: string } }).props.className;
    const match = cls.match(/language-([\w+-]+)/);
    if (match) language = match[1];
  }

  const handleCopy = () => {
    const code = preRef.current?.querySelector('code');
    if (code?.textContent) {
      navigator.clipboard.writeText(code.textContent).catch(() => {
        /* clipboard unavailable (sandboxed iframe etc) — silent */
      });
    }
  };

  return (
    <div className="my-2 rounded overflow-hidden">
      <div className="flex items-center justify-between bg-vibe-bg-tertiary px-3 py-1 text-xs text-vibe-text-secondary">
        <span className="font-mono">{language}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="hover:text-vibe-text"
          title="Copy code"
        >
          <Copy className="w-3 h-3" />
        </button>
      </div>
      <pre ref={preRef} className="bg-vibe-bg p-3 overflow-x-auto text-sm">
        {children}
      </pre>
    </div>
  );
}

/** Build the components map. Most entries are static, but the `pre:`
 * override needs access to per-render props (messageFinishReason +
 * messageIsStreaming) so it can pass them down to FileWriteCard. Built
 * fresh per render via `useMemo` (cheap — just a small object literal).
 */
function buildComponents(opts: {
  messageFinishReason?: string;
  messageIsStreaming?: boolean;
}): Components {
  return {
  h1: ({ children }) => (
    <h1 className="text-xl font-semibold mt-3 mb-2 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-lg font-semibold mt-3 mb-2 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-base font-semibold mt-2 mb-1 first:mt-0">{children}</h3>
  ),
  p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="my-2 pl-5 list-disc">{children}</ul>,
  ol: ({ children }) => <ol className="my-2 pl-5 list-decimal">{children}</ol>,
  li: ({ children }) => <li className="my-1">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="my-3 pl-3 border-l-2 border-vibe-accent text-vibe-text-secondary italic">
      {children}
    </blockquote>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      className="text-vibe-accent hover:underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="px-3 py-2 bg-vibe-bg-tertiary border border-vibe-border text-left font-semibold">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2 border border-vibe-border">{children}</td>
  ),
  hr: () => <hr className="my-4 border-vibe-border" />,
  code: ({ className, children, ...props }) => {
    // Inline code: react-markdown passes no className for inline `code`.
    // Block code (inside <pre>) carries `language-<lang>` from rehype-highlight.
    const isInline = !className;
    if (isInline) {
      return (
        <code className="px-1.5 py-0.5 bg-vibe-bg-tertiary border border-vibe-border rounded text-xs font-mono text-vibe-accent">
          {children}
        </code>
      );
    }
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children }) => {
    // 2026-05-06: when the LLM emits ```file:<path>, render a FileWriteCard
    // (with Apply-to-project button) instead of the regular CodeBlock so
    // the user can write the proposed file with one click.
    const child = Array.isArray(children) ? children[0] : children;
    const filePath = extractFileWritePath(child);
    if (filePath) {
      return (
        <FileWriteCard
          path={filePath}
          messageFinishReason={opts.messageFinishReason}
          isStreaming={opts.messageIsStreaming}
        >
          {children}
        </FileWriteCard>
      );
    }
    return <CodeBlock>{children}</CodeBlock>;
  },
  };
}

export const MarkdownRenderer = memo(function MarkdownRenderer({
  content,
  className,
  messageFinishReason,
  messageIsStreaming,
}: MarkdownRendererProps) {
  const components = useMemo(
    () => buildComponents({ messageFinishReason, messageIsStreaming }),
    [messageFinishReason, messageIsStreaming],
  );
  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});
